// Credit Card Payment Module for HobiBaca
import axios from 'axios';

/**
 * Loads the Midtrans 3DS script if not already loaded.
 * @returns {Promise<void>}
 */
export async function loadMidtrans3dsScript(clientKey) {
  if (typeof window === 'undefined') return;
  if (window.MidtransNew3ds) return;
  return new Promise((resolve, reject) => {
    const scriptId = 'midtrans-script';
    let script = document.getElementById(scriptId);
    if (script) {
      script.setAttribute('data-environment', 'sandbox');
      script.setAttribute('data-client-key', clientKey);
      resolve();
      return;
    }
    script = document.createElement('script');
    script.id = scriptId;
    script.type = 'text/javascript';
    script.src = 'https://api.midtrans.com/v2/assets/js/midtrans-new-3ds.min.js';
    script.setAttribute('data-environment', 'sandbox');
    script.setAttribute('data-client-key', clientKey);
    script.onload = resolve;
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

/**
 * Validates credit card form fields.
 * @param {Object} cardForm
 * @returns {string|null} error message or null if valid
 */
export function validateCardForm(cardForm) {
  const cardNumber = cardForm.card_number.replace(/\s+/g, '');
  if (!/^\d{13,19}$/.test(cardNumber)) {
    return 'Nomor kartu tidak valid';
  }
  if (!/^\d{2}$/.test(cardForm.card_exp_month) || !/^\d{4}$/.test(cardForm.card_exp_year)) {
    return 'Format bulan/tahun tidak valid';
  }
  if (!/^\d{3,4}$/.test(cardForm.card_cvv)) {
    return 'CVV tidak valid';
  }
  return null;
}

/**
 * Requests a card token from Midtrans.
 * @param {Object} cardForm
 * @param {Function} onSuccess
 * @param {Function} onFailure
 */
export function getCardToken(cardForm, onSuccess, onFailure) {
  if (!window.MidtransNew3ds) {
    onFailure({ status_message: 'Midtrans 3DS script belum dimuat' });
    return;
  }
  const cardData = {
    card_number: cardForm.card_number.replace(/\s+/g, ''),
    card_exp_month: cardForm.card_exp_month,
    card_exp_year: cardForm.card_exp_year,
    card_cvv: cardForm.card_cvv
  };
  window.MidtransNew3ds.getCardToken(cardData, {
    onSuccess,
    onFailure
  });
}

/**
 * Sends the token_id to backend for payment.
 * @param {string} tokenId
 * @param {string} apiBaseUrl
 * @param {string} jwtToken
 * @returns {Promise<Object>} backend response
 */
export async function sendCreditCardPayment(tokenId, apiBaseUrl, jwtToken) {
  const paymentData = {
    plan_id: 1,
    payment_gateway: 'midtrans',
    payment_method: 'credit_card',
    token_id: tokenId
  };
  const res = await axios.post(
    `${apiBaseUrl}/api/subscriptions/create/`,
    paymentData,
    {
      headers: {
        'Authorization': `Bearer ${jwtToken}`,
        'Content-Type': 'application/json'
      }
    }
  );
  return res.data;
}

/**
 * Handles 3DS authentication.
 * @param {string} redirectUrl
 * @param {Object} callbacks { onSuccess, onFailure, onPending }
 */
export function authenticate3ds(redirectUrl, callbacks) {
  if (!window.MidtransNew3ds) {
    if (callbacks.onFailure) callbacks.onFailure({ message: 'Midtrans 3DS script belum dimuat' });
    return;
  }
  window.MidtransNew3ds.authenticate(redirectUrl, {
    performAuthentication: (url) => {
      window.open(url, '_blank', 'width=500,height=600');
    },
    onSuccess: callbacks.onSuccess,
    onFailure: callbacks.onFailure,
    onPending: callbacks.onPending
  });
}